import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CalendarIcon, Users, DollarSign, Sparkles, ArrowLeft, Clock, Info, Minus, Plus } from 'lucide-react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

const CATEGORIES = [
  { id: 'yacht', name_en: 'Yachts', name_es: 'Yates', icon: '🛥️' },
  { id: 'tour', name_en: 'Tours', name_es: 'Tours', icon: '🗺️' },
  { id: 'activity', name_en: 'Activities', name_es: 'Actividades', icon: '🏄' },
  { id: 'party', name_en: 'Parties', name_es: 'Fiestas', icon: '🎉' },
  { id: 'car_rental', name_en: 'Car Rental', name_es: 'Renta de Autos', icon: '🚗' },
  { id: 'lodging', name_en: 'Lodging', name_es: 'Hospedaje', icon: '🏨' }
];

const LOCATIONS = [
  { id: 'playa_del_carmen', name: 'Playa del Carmen' },
  { id: 'cancun', name: 'Cancún' },
  { id: 'tulum', name: 'Tulum' },
  { id: 'quintana_roo', name: 'Quintana Roo' },
  { id: 'los_cabos', name: 'Los Cabos' }
];

const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
];

export default function CreateRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { language } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({ min: 0, max: 0, avg: 0, per_person: null, duration: null });
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  const [formData, setFormData] = useState({
    category: searchParams.get('category') || '',
    subcategory: searchParams.get('subcategory') || '',
    location: searchParams.get('location') || '',
    date: searchParams.get('date') ? new Date(`${searchParams.get('date')}T00:00:00`) : null,
    time: searchParams.get('time') || '',
    people_count: parseInt(searchParams.get('people_count') || '2', 10) || 2,
    notes: searchParams.get('notes') || '',
    budget_min: searchParams.get('budget_min') || '',
    budget_max: searchParams.get('budget_max') || '',
    currency: (searchParams.get('currency') || '').toUpperCase() === 'MXN' ? 'MXN' : 'USD',
  });

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category) {
        setSubcategories([]);
        return;
      }
      
      setLoadingSubcategories(true);
      try {
        const response = await api().get(`/subcategories/${formData.category}`);
        setSubcategories(response.data || []);
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setSubcategories([]);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    fetchSubcategories();
    // Reset subcategory when category changes
    setFormData(prev => ({ ...prev, subcategory: '' }));
  }, [formData.category, api]);

  // Fetch price suggestions when relevant fields change
  const fetchSuggestions = useCallback(async () => {
    if (formData.category && formData.location && formData.people_count > 0) {
      try {
        let url = `/requests/price-suggestion?category=${formData.category}&location=${formData.location}&people_count=${formData.people_count}`;
        if (formData.subcategory) {
          url += `&subcategory=${formData.subcategory}`;
        }
        const response = await api().post(url);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }
  }, [formData.category, formData.location, formData.people_count, formData.subcategory, api]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const applySuggestion = () => {
    if (!suggestions) return;
    setFormData((p) => ({
      ...p,
      budget_min: Math.round(suggestions.min),
      budget_max: Math.round(suggestions.max),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.location || !formData.date || !formData.time) {
      toast.error(language === 'en' ? 'Please fill all required fields' : 'Por favor completa todos los campos');
      return;
    }

    setIsLoading(true);
    try {
      const min = formData.budget_min === '' ? null : Number(formData.budget_min);
      const max = formData.budget_max === '' ? null : Number(formData.budget_max);
      const useSuggestedNow = min == null && max == null;
      const payload = {
        category: formData.category,
        subcategory: formData.subcategory || null,
        location: formData.location,
        date: format(formData.date, 'yyyy-MM-dd'),
        time: formData.time,
        people_count: formData.people_count,
        notes: formData.notes,
        budget_min: useSuggestedNow ? null : min,
        budget_max: useSuggestedNow ? null : max,
        currency: formData.currency || 'USD',
        use_suggested_budget: useSuggestedNow,
      };
      
      const response = await api().post('/requests', payload);
      toast.success(language === 'en' ? 'Request created!' : '¡Solicitud creada!');
      navigate(`/request/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Error' : 'Error'));
    } finally {
      setIsLoading(false);
    }
  };

  const incrementPeople = () => {
    setFormData(prev => ({ ...prev, people_count: Math.min(prev.people_count + 1, 50) }));
  };

  const decrementPeople = () => {
    setFormData(prev => ({ ...prev, people_count: Math.max(prev.people_count - 1, 1) }));
  };

  const selectedCategory = CATEGORIES.find(c => c.id === formData.category);
  const selectedSubcategory = subcategories.find(s => s.id === formData.subcategory);

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Find experience' : 'Buscar experiencia'}
        right={
          <Link to="/dashboard" data-testid="back-btn" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </Link>
        }
      />
      <main className="px-4 py-4" data-testid="create-request-page">
        {/* Banner: prefilled from Chuy chat */}
        {searchParams.get('category') || searchParams.get('location') || searchParams.get('date') || searchParams.get('people_count') ? (
          <div
            data-testid="chuy-prefill-banner"
            className="mb-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 p-3 flex items-start gap-3"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 ring-2 ring-emerald-400">
              <img src="/chuy.png" alt="Chuy" className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
                <Sparkles size={13} />
                {language === 'en' ? 'Pre-filled from your chat with Chuy' : 'Pre-llenado con tu plática con Chuy'}
              </p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-300 leading-snug mt-0.5">
                {language === 'en'
                  ? 'Edit anything you want before sending.'
                  : 'Edita lo que quieras antes de enviar.'}
              </p>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Step 1: Category */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {language === 'en' ? '1. What are you looking for?' : '1. ¿Qué estás buscando?'}
            </Label>
            <div className="grid grid-cols-3 gap-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                  className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                    formData.category === cat.id
                      ? 'border-caribbean bg-caribbean/10'
                      : 'border-border hover:border-caribbean/50 bg-card'
                  }`}
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <span className={`text-sm ${formData.category === cat.id ? 'text-caribbean font-medium' : 'text-muted-foreground'}`}>
                    {language === 'en' ? cat.name_en : cat.name_es}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Subcategory */}
          {formData.category && subcategories.length > 0 && (
            <div className="space-y-3">
              <div>
                <Label className="text-base font-semibold">
                  {language === 'en' ? '2. What type? (optional)' : '2. ¿Qué tipo? (opcional)'}
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'en' 
                    ? 'Select one to help providers understand what you want, or skip and describe in notes'
                    : 'Selecciona uno para ayudar a los proveedores, o salta y describe en las notas'}
                </p>
              </div>
              {loadingSubcategories ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-caribbean"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                  {/* Option: Other / Custom */}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, subcategory: 'other' }))}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                      formData.subcategory === 'other'
                        ? 'border-gold bg-gold/10'
                        : 'border-border hover:border-gold/50 bg-card'
                    }`}
                  >
                    <div className="text-left">
                      <p className={`font-medium ${formData.subcategory === 'other' ? 'text-gold' : ''}`}>
                        {language === 'en' ? '✨ Other / I\'ll describe it' : '✨ Otro / Lo describiré'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'en' 
                          ? 'Write what you need in the notes section below'
                          : 'Escribe lo que necesitas en las notas abajo'}
                      </p>
                    </div>
                  </button>
                  
                  {/* Subcategories list */}
                  {subcategories.map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, subcategory: sub.id }))}
                      className={`p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                        formData.subcategory === sub.id
                          ? 'border-caribbean bg-caribbean/10'
                          : 'border-border hover:border-caribbean/50 bg-card'
                      }`}
                    >
                      <div className="text-left">
                        <p className={`font-medium ${formData.subcategory === sub.id ? 'text-caribbean' : ''}`}>
                          {language === 'en' ? sub.name_en : sub.name_es}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            <Clock size={10} className="mr-1" />
                            {sub.duration}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {language === 'en' ? 'approx.' : 'aprox.'}
                        </p>
                        <p className="font-heading font-bold text-caribbean">
                          ~${sub.base_price}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sub.per_person 
                            ? (language === 'en' ? '/person' : '/persona')
                            : (language === 'en' ? '/group' : '/grupo')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Location */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {formData.category && subcategories.length > 0 ? '3.' : '2.'} {language === 'en' ? 'Where?' : '¿Dónde?'}
            </Label>
            <Select
              value={formData.location}
              onValueChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
            >
              <SelectTrigger className="h-14 rounded-xl text-base">
                <SelectValue placeholder={language === 'en' ? 'Select location' : 'Selecciona ubicación'} />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id} className="py-3">
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Step 4: People Count */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              {formData.category && subcategories.length > 0 ? '4.' : '3.'} {language === 'en' ? 'How many people?' : '¿Cuántas personas?'}
            </Label>
            <div className="flex items-center justify-center gap-6 p-4 bg-card rounded-xl border">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={decrementPeople}
                disabled={formData.people_count <= 1}
                className="h-12 w-12 rounded-full"
              >
                <Minus size={20} />
              </Button>
              <div className="flex items-center gap-2">
                <Users className="text-caribbean" size={24} />
                <span className="font-heading text-3xl font-bold w-12 text-center">
                  {formData.people_count}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={incrementPeople}
                disabled={formData.people_count >= 50}
                className="h-12 w-12 rounded-full"
              >
                <Plus size={20} />
              </Button>
            </div>
          </div>

          {/* Step 5: Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {language === 'en' ? 'Date' : 'Fecha'}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-14 justify-start text-left font-normal rounded-xl"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.date ? format(formData.date, 'PPP') : (language === 'en' ? 'Pick date' : 'Elige fecha')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {language === 'en' ? 'Time' : 'Hora'}
              </Label>
              <Select
                value={formData.time}
                onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
              >
                <SelectTrigger className="h-14 rounded-xl">
                  <SelectValue placeholder={language === 'en' ? 'Select time' : 'Elige hora'} />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price Suggestion Card */}
          {formData.category && formData.location && formData.people_count > 0 && (
            <Card className="border-0 shadow-lg bg-gradient-to-br from-caribbean/10 to-caribbean/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-caribbean rounded-xl">
                    <Sparkles className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold">
                      {language === 'en' ? 'AI Price Suggestion' : 'Precio Sugerido por IA'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {language === 'en' ? 'Based on real market prices' : 'Basado en precios reales del mercado'}
                    </p>
                  </div>
                </div>
                
                {/* Disclaimer about approximate prices */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-800 flex items-center gap-2">
                    <Info size={14} />
                    {language === 'en' 
                      ? '⚡ These are approximate reference prices. The final price depends on each provider\'s offer.'
                      : '⚡ Estos son precios aproximados de referencia. El precio final depende de la oferta de cada proveedor.'}
                  </p>
                </div>
                
                <div className="bg-card rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Estimated range' : 'Rango estimado'}
                    </span>
                    <span className="font-heading text-xl font-bold text-caribbean">
                      ${Math.round(suggestions.min)} - ${Math.round(suggestions.max)} USD
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {language === 'en' ? 'Average' : 'Promedio'}
                    </span>
                    <span className="font-semibold">${Math.round(suggestions.avg)} USD</span>
                  </div>
                  {suggestions.duration && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                      <Clock size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {language === 'en' ? 'Duration:' : 'Duración:'} {suggestions.duration}
                      </span>
                    </div>
                  )}
                  {suggestions.per_person !== null && (
                    <div className="flex items-center gap-2 mt-1">
                      <Info size={14} className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {suggestions.per_person 
                          ? (language === 'en' ? 'Price calculated per person' : 'Precio calculado por persona')
                          : (language === 'en' ? 'Price is for entire group' : 'Precio es por todo el grupo')}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Budget — always visible */}
          <div className="space-y-3 bg-card border border-border rounded-2xl p-4" data-testid="budget-section">
            <Label className="text-base font-semibold flex items-center justify-between">
              <span>{language === 'en' ? 'Set my budget' : 'Pon mi presupuesto'}</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full overflow-hidden border border-border text-[10px] font-bold" data-testid="budget-currency-toggle">
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, currency: 'USD' }))}
                    className={`px-2.5 py-1 transition-colors ${formData.currency === 'USD' ? 'bg-caribbean text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                    data-testid="budget-currency-usd"
                  >USD</button>
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, currency: 'MXN' }))}
                    className={`px-2.5 py-1 transition-colors ${formData.currency === 'MXN' ? 'bg-caribbean text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                    data-testid="budget-currency-mxn"
                  >MXN</button>
                </div>
                {suggestions && suggestions.min > 0 && (
                  <button
                    type="button"
                    onClick={applySuggestion}
                    data-testid="use-suggestion-btn"
                    className="text-xs text-caribbean font-medium hover:underline"
                  >
                    {language === 'en' ? '✨ Use AI suggestion' : '✨ Usar sugerencia IA'}
                  </button>
                )}
              </div>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-muted-foreground">{language === 'en' ? `Min (${formData.currency})` : `Mín (${formData.currency})`}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input
                    data-testid="budget-min-input"
                    type="number"
                    inputMode="numeric"
                    value={formData.budget_min}
                    placeholder={suggestions?.min ? String(Math.round(suggestions.min)) : '100'}
                    onChange={(e) => setFormData((p) => ({ ...p, budget_min: e.target.value }))}
                    className="pl-9 h-11 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">{language === 'en' ? `Max (${formData.currency})` : `Máx (${formData.currency})`}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input
                    data-testid="budget-max-input"
                    type="number"
                    inputMode="numeric"
                    value={formData.budget_max}
                    placeholder={suggestions?.max ? String(Math.round(suggestions.max)) : '500'}
                    onChange={(e) => setFormData((p) => ({ ...p, budget_max: e.target.value }))}
                    className="pl-9 h-11 rounded-xl"
                  />
                </div>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {language === 'en'
                ? 'Leave both empty to use the AI suggested range (in USD).'
                : 'Déjalos vacíos para usar el rango sugerido por la IA (en USD).'}
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              {language === 'en' ? 'Additional notes (optional)' : 'Notas adicionales (opcional)'}
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={language === 'en' 
                ? 'Any special requests or preferences...'
                : 'Alguna solicitud especial o preferencia...'
              }
              className="min-h-24 rounded-xl"
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={isLoading || !formData.category || !formData.location || !formData.date || !formData.time}
            className="w-full h-14 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold text-lg"
          >
            {isLoading 
              ? (language === 'en' ? 'Creating...' : 'Creando...') 
              : (language === 'en' ? 'Find my experience' : 'Encontrar mi experiencia')}
          </Button>
        </form>
      </main>
    </MobileShell>
  );
}
